import React from "react";
import { motion, useTransform } from "framer-motion";
import { Bookmark } from "lucide-react";

const slug = (s) =>
  s
    .toLowerCase()
    .replace(/['"`]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

/**
 * EventCard — editorial card.
 *
 * Receives optional `progress` (MotionValue 0..1 of parent phase scroll)
 * to add a subtle secondary parallax: cards drift slightly slower than
 * the page scroll (giving depth versus the fully-stuck background).
 */
const EventCard = ({
  event,
  index,
  phaseId,
  alignRight,
  total,
  progress,
  eventIndex = 0,
  totalEvents = 1,
}) => {
  const testId = `event-card-${phaseId}-${slug(event.title)}`;

  // Each event occupies a slice of the phase's scroll progress.
  // A card should "live" around its slice center. We compute local
  // progress [-1..0..1] within the card's window and translate Y
  // slightly for a floating feel.
  const segment = 1 / (totalEvents + 1);
  const center = (eventIndex + 1) * segment;
  const start = Math.max(0, center - segment);
  const end = Math.min(1, center + segment);

  const localY = useTransform(
    progress || { get: () => 0, on: () => () => {} },
    [start, center, end],
    [60, 0, -40]
  );
  const localOpacity = useTransform(
    progress || { get: () => 0, on: () => () => {} },
    [start, start + segment * 0.3, center, end - segment * 0.3, end],
    [0, 1, 1, 1, 0.35]
  );

  return (
    <motion.article
      data-testid={testId}
      style={progress ? { y: localY, opacity: localOpacity } : undefined}
      className={`pointer-events-auto w-full max-w-xl lg:max-w-2xl ${
        alignRight ? "ml-auto" : "mr-auto"
      }`}
    >
      <div
        className="relative bg-ink-2/95 backdrop-blur-md border border-warm-border p-7 md:p-10 lg:p-12"
        style={{
          boxShadow:
            "0 30px 80px -30px rgba(0,0,0,0.7), inset 0 0 0 1px rgba(232,217,184,0.05)",
        }}
      >
        <div className="absolute -top-px left-0 h-[2px] w-12 bg-copper" />
        <div className="absolute top-0 -left-px w-[2px] h-12 bg-copper" />

        <header className="flex items-start gap-4 mb-6">
          <div className="flex-shrink-0 mt-1">
            <span className="font-serif text-copper text-3xl md:text-4xl leading-none">
              {String(index + 1).padStart(2, "0")}
            </span>
            <span className="block text-[10px] uppercase tracking-[0.3em] text-warm-muted mt-1">
              /0{total}
            </span>
          </div>
          <div className="flex-1">
            <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-copper mb-3">
              <Bookmark size={12} />
              {event.date}
            </span>
            <h3 className="font-serif text-parchment text-2xl md:text-3xl lg:text-4xl leading-[1.05] tracking-tighter2">
              {event.title}
            </h3>
          </div>
        </header>

        <div className="hair my-6" />

        <p className="text-parchment-soft text-base md:text-[17px] leading-[1.85] font-light">
          {event.body}
        </p>
      </div>
    </motion.article>
  );
};

export default EventCard;
